import { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ShoppingCart, 
  Receipt, 
  Plus, 
  Minus,
  FileText,
  Package,
  Funnel,
  Bell,
  X,
  Gear,
  FileXls
} from "@phosphor-icons/react";
import { toast } from "sonner";
import axios from "axios";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format } from "date-fns";
import html2pdf from "html2pdf.js";
import * as XLSX from "xlsx";

import StatsCards from "@/components/dashboard/StatsCards";
import MonthlyChart from "@/components/dashboard/MonthlyChart";
import PurchaseForm from "@/components/dashboard/PurchaseForm";
import ConsumptionForm from "@/components/dashboard/ConsumptionForm";
import InventoryTable from "@/components/dashboard/InventoryTable";
import PurchasesTable from "@/components/dashboard/PurchasesTable";
import ConsumptionTable from "@/components/dashboard/ConsumptionTable";
import CategoryManager from "@/components/dashboard/CategoryManager";
import AlertsPanel from "@/components/dashboard/AlertsPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

export default function Dashboard() {
  const [summary, setSummary] = useState({
    total_gastado: 0,
    total_compras: 0,
    articulos_bajo_stock: 0,
    articulos_agotados: 0,
    articulos_unicos: 0,
    alertas: []
  });
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [consumption, setConsumption] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [shoppingList, setShoppingList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTicket, setShowTicket] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const [showAlerts, setShowAlerts] = useState(false);

  // Filters
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [selectedCategory, setSelectedCategory] = useState("all");

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const mesParam = selectedMonth !== "all" ? `?mes=${selectedMonth}` : "";
      const catParam = selectedCategory !== "all" ? `${mesParam ? "&" : "?"}categoria=${selectedCategory}` : "";
      const params = mesParam + catParam;

      const [
        summaryRes,
        monthlyRes,
        purchasesRes,
        consumptionRes,
        inventoryRes,
        shoppingListRes,
        categoriesRes,
        monthsRes
      ] = await Promise.all([
        axios.get(`${API}/stats/summary${params}`),
        axios.get(`${API}/stats/monthly${selectedCategory !== "all" ? `?categoria=${selectedCategory}` : ""}`),
        axios.get(`${API}/purchases${params}`),
        axios.get(`${API}/consumption`),
        axios.get(`${API}/inventory${selectedCategory !== "all" ? `?categoria=${selectedCategory}` : ""}`),
        axios.get(`${API}/shopping-list`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/stats/months`)
      ]);

      setSummary(summaryRes.data);
      setMonthlyStats(monthlyRes.data);
      setPurchases(purchasesRes.data);
      setConsumption(consumptionRes.data);
      setInventory(inventoryRes.data);
      setShoppingList(shoppingListRes.data);
      setCategories(categoriesRes.data);
      setAvailableMonths(monthsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error al cargar los datos");
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedCategory]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddPurchase = useCallback(async (purchaseData) => {
    try {
      await axios.post(`${API}/purchases`, purchaseData);
      toast.success("Compra registrada correctamente");
      fetchData();
      return true;
    } catch (error) {
      console.error("Error adding purchase:", error);
      toast.error("Error al registrar la compra");
      return false;
    }
  }, [fetchData]);

  const handleAddConsumption = useCallback(async (consumptionData) => {
    try {
      await axios.post(`${API}/consumption`, consumptionData);
      toast.success("Consumo registrado correctamente");
      fetchData();
      return true;
    } catch (error) {
      console.error("Error adding consumption:", error);
      toast.error("Error al registrar el consumo");
      return false;
    }
  }, [fetchData]);

  const handleUpdateStockPrevisto = useCallback(async (articulo, stockPrevisto) => {
    try {
      await axios.post(`${API}/stock-previsto`, {
        articulo: articulo,
        stock_previsto: parseFloat(stockPrevisto)
      });
      toast.success("Stock previsto actualizado");
      fetchData();
    } catch (error) {
      console.error("Error updating stock previsto:", error);
      toast.error("Error al actualizar el stock previsto");
    }
  }, [fetchData]);

  const handleUpdateArticleCategory = useCallback(async (articulo, categoria) => {
    try {
      await axios.post(`${API}/article-category`, {
        articulo: articulo,
        categoria: categoria
      });
      toast.success("Categoría del artículo actualizada");
      fetchData();
    } catch (error) {
      console.error("Error updating article category:", error);
      toast.error("Error al actualizar la categoría");
    }
  }, [fetchData]);

  const handleDeletePurchase = useCallback(async (id) => {
    try {
      await axios.delete(`${API}/purchases/${id}`);
      toast.success("Compra eliminada");
      fetchData();
    } catch (error) {
      console.error("Error deleting purchase:", error);
      toast.error("Error al eliminar la compra");
    }
  }, [fetchData]);

  const handleDeleteConsumption = useCallback(async (id) => {
    try {
      await axios.delete(`${API}/consumption/${id}`);
      toast.success("Consumo eliminado");
      fetchData();
    } catch (error) {
      console.error("Error deleting consumption:", error);
      toast.error("Error al eliminar el consumo");
    }
  }, [fetchData]);

  const exportToPDF = useCallback(() => {
    const element = document.getElementById("ticket-content");
    const opt = {
      margin: 0.5,
      filename: `lista-compra-${format(new Date(), "yyyy-MM-dd")}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "in", format: "letter", orientation: "portrait" }
    };
    html2pdf().set(opt).from(element).save();
    toast.success("PDF generado correctamente");
  }, []);

  const exportToExcel = useCallback(async () => {
    try {
      const mesParam = selectedMonth !== "all" ? `?mes=${selectedMonth}` : "";
      const catParam = selectedCategory !== "all" ? `${mesParam ? "&" : "?"}categoria=${selectedCategory}` : "";
      const response = await axios.get(`${API}/export/data${mesParam}${catParam}`);
      const data = response.data;

      const wb = XLSX.utils.book_new();

      // Purchases sheet
      const purchasesData = data.purchases.map(p => ({
        Fecha: p.fecha,
        Artículo: p.articulo,
        Categoría: p.categoria || "Otros",
        Cantidad: p.cantidad,
        "Precio Unit.": p.precio_articulo,
        "Total": p.precio_total
      }));
      const wsPurchases = XLSX.utils.json_to_sheet(purchasesData);
      XLSX.utils.book_append_sheet(wb, wsPurchases, "Compras");

      // Inventory sheet
      const inventoryData = data.inventory.map(i => ({
        Artículo: i.articulo,
        Categoría: i.categoria || "Otros",
        Comprado: i.cantidad_comprada,
        Consumido: i.cantidad_consumida,
        "Stock Real": i.stock_real,
        "Stock Previsto": i.stock_previsto,
        "Precio Prom.": i.precio_promedio,
        "Alerta": i.alerta_stock ? "Sí" : "No"
      }));
      const wsInventory = XLSX.utils.json_to_sheet(inventoryData);
      XLSX.utils.book_append_sheet(wb, wsInventory, "Inventario");

      // Summary sheet
      const summaryData = [{
        "Total Gastado": data.summary.total_gastado,
        "Total Compras": data.summary.total_compras,
        "Artículos Bajo Stock": data.summary.articulos_bajo_stock,
        "Artículos Agotados": data.summary.articulos_agotados,
        "Artículos Únicos": data.summary.articulos_unicos
      }];
      const wsSummary = XLSX.utils.json_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(wb, wsSummary, "Resumen");

      const filename = `smartshop-${selectedMonth !== "all" ? selectedMonth : "todos"}-${format(new Date(), "yyyy-MM-dd")}.xlsx`;
      XLSX.writeFile(wb, filename);
      toast.success("Excel exportado correctamente");
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      toast.error("Error al exportar a Excel");
    }
  }, [selectedMonth, selectedCategory]);

  const totalShoppingList = useMemo(() => 
    shoppingList.reduce((acc, item) => acc + item.total, 0), 
    [shoppingList]
  );

  const reversedPurchases = useMemo(() => 
    [...purchases].reverse(), 
    [purchases]
  );

  const reversedConsumption = useMemo(() => 
    [...consumption].reverse(), 
    [consumption]
  );

  const alertCount = summary.alertas?.length || 0;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-black text-slate-900 tracking-tight" style={{ fontFamily: "'Chivo', sans-serif" }}>
              SmartShop
            </h1>
            <div className="flex items-center gap-3">
              {/* Alerts Button */}
              <Button 
                data-testid="alerts-btn"
                variant="outline"
                className="relative"
                onClick={() => setShowAlerts(true)}
              >
                <Bell size={20} />
                {alertCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {alertCount}
                  </span>
                )}
              </Button>

              {/* Categories Button */}
              <Button 
                data-testid="categories-btn"
                variant="outline"
                onClick={() => setShowCategories(true)}
              >
                <Gear size={20} className="mr-2" />
                Categorías
              </Button>

              {/* Export Excel Button */}
              <Button 
                data-testid="export-excel-btn"
                variant="outline"
                onClick={exportToExcel}
              >
                <FileXls size={20} className="mr-2" />
                Excel
              </Button>

              {/* Shopping List Button */}
              <Dialog open={showTicket} onOpenChange={setShowTicket}>
                <DialogTrigger asChild>
                  <Button 
                    data-testid="open-shopping-list-btn"
                    className="bg-slate-900 text-white hover:bg-slate-800"
                  >
                    <Receipt className="mr-2" size={20} />
                    Lista de Compra
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Lista de Compra</DialogTitle>
                  </DialogHeader>
                  <div id="ticket-content" className="ticket-container">
                    <div className="ticket-header">
                      <h2 className="text-lg font-bold">LISTA DE COMPRA</h2>
                      <p className="text-xs text-slate-500">{format(new Date(), "dd/MM/yyyy HH:mm")}</p>
                    </div>
                    <div className="space-y-1">
                      {shoppingList.length === 0 ? (
                        <p className="text-center text-slate-500 py-4">No hay artículos pendientes</p>
                      ) : (
                        shoppingList.map((item) => (
                          <div key={item.articulo} className="ticket-item">
                            <span>{item.cantidad}x {item.articulo}</span>
                            <span>{item.total.toFixed(2)}€</span>
                          </div>
                        ))
                      )}
                    </div>
                    {shoppingList.length > 0 && (
                      <div className="ticket-total">
                        <div className="ticket-item">
                          <span className="font-bold">TOTAL</span>
                          <span className="font-bold">{totalShoppingList.toFixed(2)}€</span>
                        </div>
                      </div>
                    )}
                  </div>
                  {shoppingList.length > 0 && (
                    <Button 
                      data-testid="export-pdf-btn"
                      onClick={exportToPDF} 
                      className="w-full mt-4 bg-slate-900 text-white hover:bg-slate-800"
                    >
                      <FileText className="mr-2" size={20} />
                      Exportar a PDF
                    </Button>
                  )}
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="space-y-8"
        >
          {/* Filters */}
          <motion.div variants={itemVariants} className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <Funnel size={20} className="text-slate-500" />
              <span className="text-sm font-medium text-slate-700">Filtros:</span>
            </div>
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger data-testid="filter-month" className="w-[180px]">
                <SelectValue placeholder="Todos los meses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los meses</SelectItem>
                {availableMonths.map((month) => (
                  <SelectItem key={month} value={month}>{month}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger data-testid="filter-category" className="w-[180px]">
                <SelectValue placeholder="Todas las categorías" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las categorías</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.nombre}>{cat.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(selectedMonth !== "all" || selectedCategory !== "all") && (
              <Button
                data-testid="clear-filters-btn"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSelectedMonth("all");
                  setSelectedCategory("all");
                }}
              >
                <X size={16} className="mr-1" />
                Limpiar filtros
              </Button>
            )}
          </motion.div>

          {/* Stats Cards */}
          <motion.div variants={itemVariants}>
            <StatsCards summary={summary} />
          </motion.div>

          {/* Chart */}
          <motion.div variants={itemVariants}>
            <MonthlyChart monthlyStats={monthlyStats} />
          </motion.div>

          {/* Tabs */}
          <motion.div variants={itemVariants}>
            <Tabs defaultValue="compras" className="w-full">
              <TabsList className="bg-white border border-slate-200 p-1">
                <TabsTrigger 
                  value="compras" 
                  data-testid="tab-compras"
                  className="data-[state=active]:bg-slate-900 data-[state=active]:text-white"
                >
                  <Plus size={16} className="mr-2" />
                  Registrar Compra
                </TabsTrigger>
                <TabsTrigger 
                  value="consumo" 
                  data-testid="tab-consumo"
                  className="data-[state=active]:bg-slate-900 data-[state=active]:text-white"
                >
                  <Minus size={16} className="mr-2" />
                  Registrar Consumo
                </TabsTrigger>
                <TabsTrigger 
                  value="inventario" 
                  data-testid="tab-inventario"
                  className="data-[state=active]:bg-slate-900 data-[state=active]:text-white"
                >
                  <Package size={16} className="mr-2" />
                  Inventario
                </TabsTrigger>
              </TabsList>

              {/* Compras Tab */}
              <TabsContent value="compras">
                <Card className="dashboard-card">
                  <CardHeader>
                    <CardTitle>Nueva Compra</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <PurchaseForm 
                      onSubmit={handleAddPurchase} 
                      categories={categories}
                      onBulkUploadComplete={fetchData}
                    />
                    <PurchasesTable 
                      purchases={reversedPurchases} 
                      onDelete={handleDeletePurchase} 
                    />
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Consumo Tab */}
              <TabsContent value="consumo">
                <Card className="dashboard-card">
                  <CardHeader>
                    <CardTitle>Nuevo Consumo</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ConsumptionForm onSubmit={handleAddConsumption} />
                    <ConsumptionTable 
                      consumption={reversedConsumption} 
                      onDelete={handleDeleteConsumption} 
                    />
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Inventario Tab */}
              <TabsContent value="inventario">
                <Card className="dashboard-card">
                  <CardHeader>
                    <CardTitle>Inventario y Stock</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <InventoryTable 
                      inventory={inventory} 
                      categories={categories}
                      onUpdateStockPrevisto={handleUpdateStockPrevisto}
                      onUpdateCategory={handleUpdateArticleCategory}
                    />
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </motion.div>
        </motion.div>
      </main>

      {/* Category Manager Dialog */}
      <Dialog open={showCategories} onOpenChange={setShowCategories}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Gestión de Categorías</DialogTitle>
          </DialogHeader>
          <CategoryManager categories={categories} onUpdate={fetchData} />
        </DialogContent>
      </Dialog>

      {/* Alerts Panel */}
      <AlertsPanel 
        open={showAlerts} 
        onOpenChange={setShowAlerts} 
        alerts={summary.alertas || []} 
      />
    </div>
  );
}
